import type React from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers';
import numberUtils from 'app/shared/utils/numberUtils';
import { getListingType, getTrackingFunction } from 'app/shared/utils/clickstreamUtils';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { StyledPhoneLink } from './PhoneNumberLink.styles';

export interface PhoneNumberProps {
  ariaLabel?: string;
  className?: string;
  iconOnly?: boolean;
  listing?: ListingDetails;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  phoneIcon?: React.ReactNode | null;
  phoneNumber?: string;
}

/**
 * Component for displaying a phone number link.
 * @param ariaLabel The aria label to apply to the phone number link.
 * @param className The class name to apply to the phone number link.
 * @param iconOnly Whether to only display the phone icon. Ignored if phoneIcon is falsy.
 * @param listing The listing to use for tracking. If not provided, the listing from the Redux store will be used.
 * @param onClick The function to call when the phone number link is clicked.
 * @param phoneIcon The phone icon to display.
 * @param phoneNumber The phone number to display. If not provided, the phone number from the listing will be used.
 * @returns The phone number link component.
 */
const PhoneNumberLink: React.FC<PhoneNumberProps> = ({
  ariaLabel,
  className,
  iconOnly,
  listing,
  onClick,
  phoneIcon,
  phoneNumber,
}) => {
  const listingRdx = useSelector((state: RootReduxState) => state.currentListingDetails.currentListing);
  const listingToUse = listing ?? listingRdx ?? null;
  const phoneNumberRdx = listingToUse?.listedBy?.contactPhone;
  const phoneNumberToUse = phoneNumber ?? phoneNumberRdx;

  if (!phoneNumberToUse) {
    return null;
  }

  const displayNumber = numberUtils.getDisplayPhoneNumber(phoneNumberToUse);
  const cleanNumberForLink = phoneNumberToUse.replace(/[^\d]/g, '');

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(e);
    }

    if (listingToUse) {
      const listingType = getListingType(listingToUse);
      if (listingType) {
        getTrackingFunction({ type: listingType, leadType: 'phone' })?.({
          listing: listingToUse,
          triggerLocation: 'home_details',
          triggerObject: 'home_details_component|summary',
        });
      }
    }
  };

  return (
    <StyledPhoneLink
      href={`tel:${cleanNumberForLink}`}
      className={className}
      onClick={handleClick}
      aria-label={ariaLabel || `Call ${displayNumber}`}
    >
      {phoneIcon && iconOnly ? null : displayNumber}
      {phoneIcon}
    </StyledPhoneLink>
  );
};

export default PhoneNumberLink;
